import type { FullShipment } from '@fv/client-types'

import { usePermissions } from '@/features/auth'
import { loadCanBeBooked } from '@/features/book/bookFuncs'
import type { BookingMode, ReferrerType } from '@/features/booking/types'
import { useUnifiedShipmentFlow } from '@/features/route-guides/hooks'
import { routes } from '@/routes'

type MyShipment = Pick<
  FullShipment,
  | 'loadId'
  | 'quoteRequestId'
  | 'selectedQuote'
  | 'workflow'
  | 'status'
  | 'pickup'
>

interface ComposeOpts {
  shipment: MyShipment
  referrer: ReferrerType
  mode?: BookingMode
  unifiedFlow: boolean
}

export const composeEditShipmentUrl = ({
  shipment,
  referrer,
  mode,
  unifiedFlow,
}: ComposeOpts): string | undefined => {
  const { selectedQuote, loadId, quoteRequestId } = shipment
  if (!selectedQuote) return

  if (unifiedFlow) {
    if (loadCanBeBooked(shipment)) {
      return routes.vendorBook(loadId, selectedQuote._id)
    } else {
      return routes.editLoad(loadId)
    }
  }

  if (shipment.workflow === 'truckload') {
    return routes.bookTruckload({
      mode,
      quoteRequestId,
      referrer,
      selectedQuotes: { [loadId]: selectedQuote._id },
    })
  }

  return routes.book({
    loadId,
    quoteId: selectedQuote._id,
  })
}

export function useEditBookingPage(shipment?: MyShipment) {
  const { canBook } = usePermissions()
  const unifiedFlow = useUnifiedShipmentFlow().isEnabled(shipment?.workflow)
  if (!shipment) {
    return { canEdit: false, composeEditLink: () => '' }
  }
  const { selectedQuote } = shipment
  const hasValidationError =
    shipment.pickup?.status === 'error' &&
    shipment.pickup?.errorType === 'validation'
  const canEdit =
    !!selectedQuote?._id &&
    canBook &&
    shipment.status !== 'canceled' &&
    (shipment.workflow !== 'parcel' || hasValidationError)

  const composeEditLink = (referrer?: ReferrerType, mode?: BookingMode) =>
    composeEditShipmentUrl({ shipment, referrer, mode, unifiedFlow })

  return {
    canEdit,
    composeEditLink,
  }
}
